/**
 * Variations de stock saisies à la main (fiche, état des stocks, réassort) : elles
 * apparaissent dans le journal des mouvements comme les ventes et les retours.
 * Valeurs alignées sur les CHECK de stock_movements ('in', 'adjustment', 'manual', 'purchase').
 */
import { supabase } from '@/integrations/supabase/client';
import { ok, err } from '@/api/result';
import type { Result } from '@/api/result';
import { appErrorFromSupabase } from '@/api/errors';
import type { AppError } from '@/api/errors';

type ManualStockChange = {
  tenantId: string;
  productId: string;
  before: number;
  after: number;
  kind: 'adjustment' | 'restock';
};

/** Journalise sans bloquer : le stock est déjà enregistré, un échec ici ne doit pas l'annuler. */
export const logManualStockChange = async (change: ManualStockChange): Promise<void> => {
  const restock = change.kind === 'restock';
  const { error } = await supabase.from('stock_movements').insert({
    tenant_id: change.tenantId,
    product_id: change.productId,
    movement_type: restock ? 'in' : 'adjustment',
    quantity: change.after - change.before,
    quantity_before: change.before,
    quantity_after: change.after,
    reference_type: restock ? 'purchase' : 'manual',
    notes: restock ? 'Réassort' : 'Correction manuelle',
  });
  if (error) console.error('[stock] mouvement non journalisé', error.code);
};

/** Variation appliquée au stock verrouillé en base : une vente récente n'est jamais écrasée. */
export const addProductStock = async (
  productId: string,
  delta: number,
  kind: ManualStockChange['kind'] = 'adjustment',
): Promise<Result<number, AppError>> => {
  const { data, error } = await supabase.rpc('add_product_stock', {
    p_product_id: productId,
    p_delta: delta,
    p_kind: kind,
  });
  if (error) return err(appErrorFromSupabase(error, 'addProductStock'));
  return ok(data);
};

/** Mode rafale : variation portant la clé du scan, appliquée une seule fois même si renvoyée. */
export const addProductStockKeyed = async (
  productId: string,
  delta: number,
  key: string,
  kind: ManualStockChange['kind'],
  note?: string,
): Promise<Result<number, AppError>> => {
  const { data, error } = await supabase.rpc('add_product_stock_keyed', {
    p_product_id: productId,
    p_delta: delta,
    p_key: key,
    p_kind: kind,
    ...(note ? { p_note: note } : {}),
  });
  if (error) return err(appErrorFromSupabase(error, 'addProductStockKeyed'));
  return ok(data);
};
